import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal, MessageSquarePlus, LogIn } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useAppSession } from '@/contexts/SessionContext';
import { useAnonHome } from '@/hooks/useAnonHome';
import { AnonSignInPrompt } from '@/components/AnonSignInPrompt';
import {
  PAPER_CONTROL_RADIUS_CLASS,
  PAPER_ICON_CONTROL_CLASS,
  PAPER_ICON_CLASS,
  PAPER_ICON_SMALL_CLASS,
  PAPER_ICON_STROKE_WIDTH,
  PAPER_MOBILE_HEADER_GRID_CLASS,
  PAPER_MOBILE_HEADER_TITLE_CAP_CLASS,
} from './paperShapes';

interface PaperChatTopBarProps {
  title: string;
}

interface MenuPosition {
  top: number;
  right: number;
  minWidth: number;
}

export const PaperChatTopBar = (props: PaperChatTopBarProps) => {
  const auth = useAuth();
  const { artifacts } = useAppSession();
  // Drive "New chat" through the anon-aware home handler so an
  // anonymous user with an active session is offered the
  // sign-in-or-continue prompt instead of silently nuking their
  // chat. The hook owns its own showPrompt state, so the prompt
  // we render below is local to this top bar — independent of
  // any other AnonSignInPrompt rendered higher up in the layout.
  const { onHome, anonPrompt } = useAnonHome();

  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<MenuPosition | null>(null);

  // Hide the trigger entirely when the artifact panel is open — the
  // panel owns the top chrome in that case (Back button + title).
  const triggerVisible = !artifacts.artifactOpen;

  // Auto-close if the panel takes over while the menu is open.
  useEffect(() => {
    if (!triggerVisible && open) setOpen(false);
  }, [triggerVisible, open]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const computePos = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPos({
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
        minWidth: Math.max(rect.width, 160),
      });
    };
    computePos();
    window.addEventListener('resize', computePos);
    window.addEventListener('scroll', computePos, true);
    return () => {
      window.removeEventListener('resize', computePos);
      window.removeEventListener('scroll', computePos, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleNewChat = () => {
    setOpen(false);
    onHome();
  };
  const handleSignIn = () => {
    setOpen(false);
    auth.signIn();
  };

  return (
    <div
      className={cn(
        'shrink-0 border-b border-solid border-[var(--hair)]',
      )}
    >
      <div
        className={cn(
          'desktop:hidden grid items-center gap-[8px]',
          PAPER_MOBILE_HEADER_GRID_CLASS,
          'px-[12px] py-[10px]',
        )}
      >
        <div className="justify-self-start min-w-0 h-[28px] flex items-center">
          <span className="text-[11px] tracking-[0.14em] uppercase text-[var(--fg-3)]">
            Agent
          </span>
        </div>

        <span
          className={cn(
            'justify-self-center min-w-0 text-[15px] text-[var(--fg)] truncate',
            PAPER_MOBILE_HEADER_TITLE_CAP_CLASS,
          )}
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {props.title}
        </span>
        <div className="justify-self-end min-w-0 h-[28px] flex items-center justify-end">
          {/* Mobile-only ellipsis menu. Hidden when the artifact panel has
              taken over the chrome. */}
          {triggerVisible && (
            <button
              ref={triggerRef}
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={open}
              aria-label="Menu"
              className={cn(
                'shrink-0',
                PAPER_ICON_CONTROL_CLASS,
              )}
            >
              <MoreHorizontal className={PAPER_ICON_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
            </button>
          )}
        </div>
      </div>

      <div className="hidden desktop:flex items-center justify-between px-[22px] py-[14px]">
        <div className="flex items-center gap-[10px] min-w-0 h-[28px]">
          <span className="text-[11px] tracking-[0.14em] uppercase text-[var(--fg-3)]">
            Agent
          </span>
          <span
            className="text-[16px] text-[var(--fg)] truncate"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {props.title}
          </span>
        </div>
      </div>

      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label="Chat menu"
          style={{
            position: 'fixed',
            top: pos?.top ?? -9999,
            right: pos?.right ?? -9999,
            minWidth: pos?.minWidth ?? 160,
            visibility: pos ? 'visible' : 'hidden',
            zIndex: 80,
          }}
          className={cn(
            'flex flex-col p-[4px] rounded-lg shadow-lg',
            'bg-[var(--surface)] border border-solid border-[var(--hair)]',
          )}
        >
          <button
            type="button"
            role="menuitem"
            onClick={handleNewChat}
            className={cn(
              'flex items-center gap-[10px] w-full px-[10px] py-[8px]',
              PAPER_CONTROL_RADIUS_CLASS,
              'text-[13px] text-[var(--fg-2)] cursor-pointer text-left',
              'transition-colors duration-150',
              'hover:bg-[var(--bg-alt)] hover:text-[var(--fg)]',
            )}
          >
            <MessageSquarePlus
              className={cn('shrink-0', PAPER_ICON_SMALL_CLASS)}
              strokeWidth={PAPER_ICON_STROKE_WIDTH}
            />
            New chat
          </button>
          {!auth.isAuthenticated && (
            <button
              type="button"
              role="menuitem"
              onClick={handleSignIn}
              className={cn(
                'flex items-center gap-[10px] w-full px-[10px] py-[8px]',
                PAPER_CONTROL_RADIUS_CLASS,
                'text-[13px] text-[var(--fg-2)] cursor-pointer text-left',
                'transition-colors duration-150',
                'hover:bg-[var(--bg-alt)] hover:text-[var(--fg)]',
              )}
            >
              <LogIn
                className={cn('shrink-0', PAPER_ICON_SMALL_CLASS)}
                strokeWidth={PAPER_ICON_STROKE_WIDTH}
              />
              Sign in
            </button>
          )}
        </div>,
        document.body,
      )}

      {anonPrompt.visible && (
        <AnonSignInPrompt
          onSignIn={anonPrompt.onSignIn}
          onContinue={anonPrompt.onContinue}
          onCancel={anonPrompt.onCancel}
        />
      )}
    </div>
  );
};
